import { computed, watch, type Ref } from 'vue'
import { useMutation, useQuery } from '/@/core/graphql'
import { gql, type WatchQueryFetchPolicy } from '@apollo/client/core'
import type {
  Project,
  ProjectClientType,
  ProjectVisibility,
  ProjectProductDisplayType,
  ProjectCategory,
  ProjectReason,
  ProjectType,
  ProjectStatus,
  ProjectThingsLayout,
} from '../types'
import { projectFragment, projectQuery } from './fragments'
import { designerProjectsFragment } from '/@/data/designer/fragments'
import { useClientCache } from '/@/common/clients/utils'
import { useRollbar } from '/@/core/rollbar'
import { getAccountsDataQuery } from '/@/common/accounts/api'
import { evictFromCache } from '/@/utils/apollo'
import type { ProjectPermissionType } from '/@/common/projects/types'
import { pageInfoFragment, type PaginatedList } from '/@/utils/pagination'

//#region query

export interface Modifiers {
  permissionTypes?: ProjectPermissionType[]
  designerIds?: string[]
  projectStatuses?: ProjectStatus[]
  projectClientTypes?: ProjectClientType[]
  text?: string
  first?: number
  after?: string
}

export const getProjectsListExistsGqlQuery = gql`
  query getDesignerProjectListExists(
    $permissionTypes: [ProjectPermissionType!]
    $designerIds: [String!]
    $projectStatuses: [ProjectStatus!]
    $projectClientTypes: [ProjectClientType!]
    $text: String
  ) {
    user {
      id
      designer {
        id
        hasProjects(
          permissionTypes: $permissionTypes
          designerIds: $designerIds
          projectStatuses: $projectStatuses
          projectClientTypes: $projectClientTypes
          text: $text
        )
      }
    }
  }
`

export const getProjectsListCountGqlQuery = gql`
  query getDesignerProjectListCount(
    $permissionTypes: [ProjectPermissionType!]
    $designerIds: [String!]
    $projectStatuses: [ProjectStatus!]
    $projectClientTypes: [ProjectClientType!]
    $text: String
  ) {
    user {
      id
      designer {
        id
        projectCount(
          permissionTypes: $permissionTypes
          designerIds: $designerIds
          projectStatuses: $projectStatuses
          projectClientTypes: $projectClientTypes
          text: $text
        )
      }
    }
  }
`

export const getProjectsListGqlQuery = gql`
  ${projectFragment}
  ${pageInfoFragment}
  query getDesignerProjectList(
    $permissionTypes: [ProjectPermissionType!]
    $designerIds: [String!]
    $projectStatuses: [ProjectStatus!]
    $projectClientTypes: [ProjectClientType!]
    $text: String
    $first: Int
    $after: String
  ) {
    user {
      id
      designer {
        id
        projectList(
          permissionTypes: $permissionTypes
          designerIds: $designerIds
          projectStatuses: $projectStatuses
          projectClientTypes: $projectClientTypes
          text: $text
          first: $first
          after: $after
        ) {
          pageInfo {
            ...PageInfoProps
          }
          edges {
            node {
              ...ProjectProps
            }
          }
        }
      }
    }
  }
`

export function useProjectsList(modifiers: Ref<Modifiers>) {
  const { result, loading, refetch, fetchMore } = useQuery<{
    user?: {
      designer: {
        projectList: PaginatedList<Project>
      }
    }
  }>(
    getProjectsListGqlQuery,
    computed(() => ({
      permissionTypes: modifiers.value.permissionTypes,
      designerIds: modifiers.value.designerIds,
      projectStatuses: modifiers.value.projectStatuses,
      projectClientTypes: modifiers.value.projectClientTypes,
      text: modifiers.value.text,
      first: modifiers.value.first,
      after: modifiers.value.after,
    })),
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const projects = computed(
    () =>
      result.value?.user?.designer?.projectList?.edges.map(e => e.node) ?? [],
  )
  const pageInfo = computed(
    () => result.value?.user?.designer?.projectList?.pageInfo,
  )

  return { projects, pageInfo, loading, refetch, fetchMore }
}

export function useProjectsListExists(modifiers: Ref<Modifiers>) {
  const { result, loading, refetch } = useQuery<{
    user?: {
      designer: {
        hasProjects: boolean
      }
    }
  }>(
    getProjectsListExistsGqlQuery,
    computed(() => ({
      permissionTypes: modifiers.value.permissionTypes,
      projectStatuses: modifiers.value.projectStatuses,
      projectClientTypes: modifiers.value.projectClientTypes,
      text: modifiers.value.text,
    })),
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const hasProjects = computed(
    () => result.value?.user?.designer?.hasProjects ?? false,
  )

  return { hasProjects, loading, refetch }
}

export function useProjectsListCount(modifiers: Ref<Modifiers>) {
  const { result, loading, refetch } = useQuery<{
    user?: {
      designer: {
        projectCount: number
      }
    }
  }>(
    getProjectsListCountGqlQuery,
    computed(() => ({
      permissionTypes: modifiers.value.permissionTypes,
      projectStatuses: modifiers.value.projectStatuses,
      projectClientTypes: modifiers.value.projectClientTypes,
      text: modifiers.value.text,
    })),
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const projectCount = computed(
    () => result.value?.user?.designer?.projectCount ?? 0,
  )

  return { projectCount, loading, refetch }
}

export function useProjectData(
  projectId: Ref<string | undefined | null>,
  fetchPolicy?: WatchQueryFetchPolicy | undefined,
  debug?: { isDebugDisabled: boolean },
) {
  // TODO: @chicks - Temp for debugging
  const rollbar = useRollbar()
  watch(
    () => projectId.value,
    id => {
      if (debug?.isDebugDisabled) return
      try {
        if (!id) throw new Error(`projectId is undefined: ${projectId.value}`)
        if (Number.isNaN(parseInt(id, 10)))
          throw new Error(`projectId is NaN: ${projectId.value}`)
      } catch (error) {
        rollbar.error(
          `Non-numeric projectId: ${projectId.value} provided to useProjectData`,
          error,
        )
      }
    },
  )

  watch(
    projectId,
    id => {
      if (!id) return
      if (Number.isNaN(parseInt(id, 10))) {
        rollbar.error(`We sent a non-numeric projectId string with a query`, {
          projectId: id,
        })
      }
    },
    { immediate: true },
  )
  // TODO: @chicks - END Temp for debugging

  const { result, loading, error, refetch } = useQuery<{ project: Project }>(
    projectQuery,
    () => ({
      projectId: projectId.value,
    }),
    () => ({
      enabled: Boolean(projectId?.value),
      fetchPolicy: fetchPolicy ?? 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    }),
  )

  const project = computed(() => result.value?.project)

  return { project, loading, error, refetch }
}
//#endregion

//#region mutations
interface SectionOrderInput {
  projectId: string
  sectionIds: string[]
}

export interface ProjectInput {
  name: string
  visibility: ProjectVisibility
  clientType?: ProjectClientType
  productDisplayType?: ProjectProductDisplayType
  category?: ProjectCategory
  reason?: ProjectReason
  projectType?: ProjectType
  status?: ProjectStatus
  taxPercentage?: number
  markupPercentage?: number
  thingsLayout?: ProjectThingsLayout
}

interface UpdateNameInput {
  projectId: string
  name: string
}

interface UpdateCoverInput {
  projectId: string
  imageUrl?: string
  note?: string
}

interface ProjectStatusInput {
  projectId: string
  status: ProjectStatus
}

export function useProjectApi() {
  const clientCache = useClientCache()

  const { mutate: create } = useMutation<{
    project: {
      create: {
        status: number
        recordId?: string
        record?: Project
      }
    }
  }>(
    gql`
      ${designerProjectsFragment}
      mutation createNewProject($input: ProjectInput!) {
        project {
          create(input: $input) {
            recordId
            status
            record {
              ...DesignerProjectsProps
            }
          }
        }
      }
    `,
    {
      refetchQueries: [{ query: getAccountsDataQuery }],
      update(cache, { data }) {
        const designerId = data?.project?.create?.record?.designer?.id
        if (designerId) {
          evictFromCache(cache, designerId, 'Designer', 'projectList')
          evictFromCache(cache, designerId, 'Designer', 'hasProjects')
          evictFromCache(cache, designerId, 'Designer', 'projectCount')
        }
      },
    },
  )

  const { mutate: update } = useMutation(
    gql`
      ${designerProjectsFragment}
      mutation updateProjectSettings(
        $projectId: String!
        $input: ProjectInput!
      ) {
        project {
          update(id: $projectId, input: $input) {
            status
            record {
              ...DesignerProjectsProps
            }
          }
        }
      }
    `,
    {
      refetchQueries: [{ query: getAccountsDataQuery }],
    },
  )

  const { mutate: updateSectionsOrder } = useMutation(gql`
    mutation updateProjectSectionsOrder($input: ProjectSectionsInput!) {
      project {
        updateSectionsOrdering(input: $input) {
          recordId
          status
          record {
            id
            sections {
              id
            }
          }
        }
      }
    }
  `)

  const { mutate: deleteProject } = useMutation<{
    project: { delete: { status: number; recordId?: string } }
  }>(gql`
    mutation deleteProject($id: String!) {
      project {
        delete(input: $id) {
          recordId
          status
        }
      }
    }
  `)

  const { mutate: updateStatus } = useMutation(
    gql`
      ${designerProjectsFragment}
      mutation updateProjectStatus($input: ProjectStatusInput!) {
        project {
          updateStatus(input: $input) {
            status
            record {
              ...DesignerProjectsProps
            }
          }
        }
      }
    `,
    {
      refetchQueries: [{ query: getAccountsDataQuery }],
    },
  )

  const { mutate: updateName } = useMutation(gql`
    mutation updateProjectName($input: UpdateProjectNameInput!) {
      project {
        updateName(input: $input) {
          status
          record {
            id
            name
          }
        }
      }
    }
  `)
  const { mutate: updateCover } = useMutation(gql`
    mutation updateProjectCover($input: UpdateCoverInput!) {
      project {
        cover {
          update(input: $input) {
            status
            record {
              id
              cover {
                imageUrl
                note
              }
            }
          }
        }
      }
    }
  `)

  return {
    async create(input: ProjectInput): Promise<Project | undefined> {
      const response = await create({ input })
      return response?.data?.project.create.record
    },
    async update(projectId: string, input: ProjectInput) {
      await update({ projectId, input })
    },
    async updateSectionsOrder(input: SectionOrderInput) {
      await updateSectionsOrder({ input })
    },
    async updateName(input: UpdateNameInput) {
      await updateName({ input })
    },
    async updateCover(input: UpdateCoverInput) {
      await updateCover({ input })
    },
    async deleteProject(id: string) {
      await deleteProject(
        { id },
        {
          refetchQueries: [{ query: getAccountsDataQuery }],
          update(cache) {
            evictFromCache(cache, id, 'Project')
            clientCache.deleteClientProject(cache, id)
          },
        },
      )
    },
    async updateStatus(input: ProjectStatusInput) {
      await updateStatus({ input })
    },
  }
}

export { useCommonApi } from './common'
//#endregion
